import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UserApi } from '../../services/api/UserApi';
import { OrganizationApi } from '../../services/ApiService';

const ROLE_OPTIONS = [
  { value: 'org_member', label: 'Member' },
  { value: 'course_admin', label: 'Course Admin' },
  { value: 'org_admin', label: 'Org Admin' },
];

const roleBadgeStyle = (role) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
  background: role === 'org_admin' ? '#fef3c7' : role === 'course_admin' ? '#dbeafe' : '#e5e7eb',
  color: role === 'org_admin' ? '#92400e' : role === 'course_admin' ? '#1e40af' : '#374151',
});

/**
 * Settings > Users > Organizations for a specific user.
 * Route: /settings/users/:userId/organizations
 * Shows the user's org memberships with ability to add/remove.
 */
function UserOrganizationsPage() {
  const { userId } = useParams();
  const [userName, setUserName] = useState('');
  const [memberships, setMemberships] = useState([]);
  const [allOrgs, setAllOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ organizationId: '', orgRole: 'org_member' });
  const [formError, setFormError] = useState(null);

  const loadMemberships = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await UserApi.getUserMemberships(userId);
      if (!resp.ok) { setError(`Failed to load memberships (${resp.status})`); return; }
      setMemberships(await resp.json());
    } catch (e) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadMemberships();
    // Load user name
    UserApi.getUser(userId).then(async resp => {
      if (resp.ok) {
        const u = await resp.json();
        setUserName(u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email || `User #${userId}`);
      }
    }).catch(() => {});
    // Load all orgs for the add dropdown
    OrganizationApi.getOrganizations().then(async resp => {
      if (resp.ok) setAllOrgs(await resp.json());
    }).catch(() => {});
  }, [userId, loadMemberships]);

  const handleRemove = async (membershipId) => {
    if (!window.confirm('Remove this user from the organization?')) return;
    setError(null);
    const resp = await UserApi.removeUserFromOrganization(userId, membershipId);
    if (!resp.ok && resp.status !== 204) { setError(`Failed to remove (${resp.status})`); return; }
    loadMemberships();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.organizationId) return;
    setAdding(true);
    setFormError(null);
    try {
      const resp = await UserApi.addUserToOrganization(userId, Number(addForm.organizationId), addForm.orgRole);
      if (resp.status === 409) { setFormError('User is already a member of this organization.'); return; }
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        setFormError(body?.error || `Failed to add (${resp.status})`);
        return;
      }
      setAddForm({ organizationId: '', orgRole: 'org_member' });
      setShowForm(false);
      loadMemberships();
    } catch (err) {
      setFormError(err?.message || 'Network error');
    } finally {
      setAdding(false);
    }
  };

  // Filter out orgs the user is already in
  const memberOrgIds = new Set(memberships.map(m => m.organizationId || m.organization_id));
  const availableOrgs = allOrgs.filter(o => !memberOrgIds.has(o.organizationId || o.id));

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>
          <Link to="/settings/users" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.9em' }}>
            Users
          </Link>
          {' › '}
          {userName || `User #${userId}`}
          {' › Organizations'}
        </h2>
        <button type="button" onClick={() => setShowForm(f => !f)}>
          {showForm ? 'Cancel' : '+ Add to Organization'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {formError && <div className="error-message">{formError}</div>}

      {showForm && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1rem', padding: '1rem', background: 'var(--light-color, #f8f9fa)', borderRadius: '8px', border: '1px solid var(--border-color, #e1e5e9)' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>Organization</label>
            <select value={addForm.organizationId} onChange={e => setAddForm(f => ({ ...f, organizationId: e.target.value }))} required>
              <option value="">Select organization…</option>
              {availableOrgs.map(o => (
                <option key={o.organizationId || o.id} value={o.organizationId || o.id}>
                  {o.name || o.organizationName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>Role</label>
            <select value={addForm.orgRole} onChange={e => setAddForm(f => ({ ...f, orgRole: e.target.value }))}>
              {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <button type="submit" disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
        </form>
      )}

      {loading ? (
        <div className="loading-state"><div className="loading-spinner" /><p>Loading memberships…</p></div>
      ) : memberships.length === 0 ? (
        <div className="empty-state"><h3>No organizations</h3><p>This user is not a member of any organization yet.</p></div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Role</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map(m => {
              const orgId = m.organizationId || m.organization_id;
              const mid = m.membershipId || m.id;
              const orgName = m.organizationName || m.organization?.name || `Org #${orgId}`;
              const role = m.orgRole || m.org_role || 'org_member';
              return (
                <tr key={mid}>
                  <td data-label="Organization">
                    <Link to={`/settings/organizations/${orgId}/members`}><strong>{orgName}</strong></Link>
                  </td>
                  <td data-label="Role"><span style={roleBadgeStyle(role)}>{ROLE_OPTIONS.find(r => r.value === role)?.label || role}</span></td>
                  <td data-label="Status">{m.status || '—'}</td>
                  <td data-label="Joined">{m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}</td>
                  <td data-label="Actions">
                    <button type="button" style={{ background: 'transparent', color: '#dc2626', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 8px' }}
                      onClick={() => handleRemove(mid)}>Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default UserOrganizationsPage;
